import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getUrl } from 'aws-amplify/storage';
import { put } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

interface FoodItem {
  name: string;
  calories: number;
}

interface MealData {
  mealTimestamp: string;
  totalCalories: number;
  confidenceLevel: string;
  foodItems: FoodItem[];
  imageKey: string;
  timestamp: number;
  date: string;
  dishName?: string;
  suggestedCalories?: number;
}

interface ApiResponse {
  message: string;
  mealData: MealData;
}

const EditMealAnalysis: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [originalMealData, setOriginalMealData] = useState<MealData | null>(location.state?.mealData || null);
  const [editedMealData, setEditedMealData] = useState<MealData | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    console.log(originalMealData);
    // If meal data wasn't passed in location state, redirect back
    if (!originalMealData) {
      navigate('/history');
      return;
    }

    // Initialize edited data with original data
    if (originalMealData && !editedMealData) {
      setEditedMealData({...originalMealData});
    }
  }, [originalMealData, editedMealData, navigate]);

  useEffect(() => {
    // Fetch image from S3 if we have meal data with an image key
    if (originalMealData?.imageKey) {
      const fetchImage = async () => {
        try {
          const { url } = await getUrl({
            key: originalMealData.imageKey,
            options: {
              contentType: 'image/jpeg',
              accessLevel: 'private',
              expiresIn: 3600 // 1 hour expiration
            }
          });
          setImageUrl(url.toString());
        } catch (error) {
          console.error('Error fetching image:', error);
          // Don't set error state here, as the meal data is still useful without the image
        }
      };

      fetchImage();
    }
  }, [originalMealData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (editedMealData) {
      setEditedMealData({
        ...editedMealData,
        [name]: name === 'totalCalories' ? parseInt(value) : value
      });
    }
  };

  const handleFoodItemChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editedMealData) return;
    
    const { name, value } = e.target;
    const updatedFoodItems = [...editedMealData.foodItems];
    updatedFoodItems[index] = {
      ...updatedFoodItems[index],
      [name]: value
    };
    
    setEditedMealData({
      ...editedMealData,
      foodItems: updatedFoodItems
    });
  };

  const handleDeleteFoodItem = (index: number) => {
    if (!editedMealData) return;
    
    const updatedFoodItems = [...editedMealData.foodItems];
    updatedFoodItems.splice(index, 1);
    
    setEditedMealData({
      ...editedMealData,
      foodItems: updatedFoodItems
    });
  };

  const handleAddFoodItem = () => {
    if (!editedMealData) return;
    
    const newFoodItem = { name: '', calories: 0 };
    setEditedMealData({
      ...editedMealData,
      foodItems: [...editedMealData.foodItems, newFoodItem]
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
      if (!editedMealData || !editedMealData.mealTimestamp) return;
    
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      // Get authentication session
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();

      if (!authToken) {
        throw new Error('Not authenticated');
      }

      // Prepare the request body as a string
      const requestBody = {
        dishName: editedMealData.dishName || '',
        totalCalories: editedMealData.totalCalories,
        foodItems: editedMealData.foodItems.map(item => ({
          name: item.name,
          calories: item.calories
        })),
        suggestedCalories: (originalMealData?.suggestedCalories ?? originalMealData?.totalCalories) ?? 0
      };
      console.log(originalMealData?.mealTimestamp);
      // Update meal data via API
      const restOperation = put({
        apiName: 'FoodTrackerAPI',
        path: `/food/meals/${encodeURIComponent(originalMealData?.mealTimestamp || '')}`,
        options: {
          body: requestBody,
          headers: {
            Authorization: authToken,
            'Content-Type': 'application/json'
          }
        }
      });

      const { body } = await restOperation.response;
      const jsonData = await body.json();
      const responseData = jsonData as unknown as ApiResponse;
      
      setSuccessMessage(responseData.message || 'Meal data updated successfully');
      setOriginalMealData(responseData.mealData);
      setEditedMealData(responseData.mealData);
      
      // Return to analysis page after brief delay
      setTimeout(() => {
        navigate(`/analysis`, { state: { mealData: responseData.mealData } });
      }, 1500);
    } catch (error) {
      console.error('Error updating meal data:', error);
      setError('Failed to update meal data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!editedMealData) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <h2 className="card-title">Edit Meal Analysis</h2>
        
        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="dishName" className="form-label">Dish Name:</label>
            <input
              id="dishName"
              type="text"
              name="dishName"
              value={editedMealData.dishName || ''}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter dish name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="totalCalories" className="form-label">Total Calories:</label>
            <input
              id="totalCalories"
              type="number"
              name="totalCalories"
              value={editedMealData.totalCalories}
              onChange={handleInputChange}
              className="form-input"
              min="0"
            />
          </div>

          <div className="form-group">
            <label htmlFor="suggestedCalories" className="form-label">Suggested Calories:</label>
            <input
              id="suggestedCalories"
              type="number"
              name="suggestedCalories"
              value={originalMealData?.suggestedCalories ?? originalMealData?.totalCalories}
              className="form-input"
              min="0"
              placeholder="Suggested calories"
              readOnly
            />
          </div>
          
          <h3 className="section-title">Food Items</h3>
          <div className="food-items-edit">
            {editedMealData.foodItems.map((item, index) => (
              <div key={index} className="food-item-edit" style={{ marginBottom: '8px' }}>
                <div className="food-item-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="text"
                    name="name"
                    value={item.name}
                    onChange={(e) => handleFoodItemChange(index, e)}
                    className="form-input"
                    placeholder="Food name"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => handleDeleteFoodItem(index)}
                    className="btn btn-icon danger"
                    aria-label="Delete item"
                    style={{ flexShrink: 0 }}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddFoodItem}
              className="btn btn-secondary"
              style={{ marginTop: '10px' }}
            >
              Add Food Item
            </button>
          </div>
          
          {imageUrl && (
            <div className="form-group">
              <h3 className="section-title">Food Image</h3>
              <img 
                src={imageUrl} 
                alt="Food" 
                style={{ width: '100%', borderRadius: '8px', marginBottom: '1rem' }}
              />
            </div>
          )}
          
          <div className="action-buttons" style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between' }}>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => navigate(`/analysis`, { state: { mealData: originalMealData } })}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  <span style={{ marginLeft: '10px' }}>Saving...</span>
                </>
              ) : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMealAnalysis;
